import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { FrameVariantScope } from "@/components/ui/frame"
import {
  DialogNestingContext,
  dialogPushBackClasses,
  useDialogNestingRoot,
  useHasOpenChildDialog,
} from "@/components/ui/dialog-nesting"

/**
 * @example
 * import { Button } from "@/components/ui/button"
 * import {
 *   Dialog,
 *   DialogClose,
 *   DialogContent,
 *   DialogDescription,
 *   DialogFooter,
 *   DialogHeader,
 *   DialogTitle,
 *   DialogTrigger,
 * } from "@/components/ui/dialog"
 * import { Input } from "@/components/ui/input"
 * import { Label } from "@/components/ui/label"
 *
 * export function RenameCampaign({ name, onSave }: { name: string; onSave: (name: string) => void }) {
 *   return (
 *     <Dialog>
 *       <DialogTrigger asChild>
 *         <Button variant="secondary">Rename</Button>
 *       </DialogTrigger>
 *       <DialogContent>
 *         <form
 *           className="flex flex-col gap-4"
 *           onSubmit={(e) => {
 *             e.preventDefault()
 *             onSave(String(new FormData(e.currentTarget).get("name")))
 *           }}
 *         >
 *           <DialogHeader>
 *             <DialogTitle>Rename campaign</DialogTitle>
 *             <DialogDescription>Only your team sees this name.</DialogDescription>
 *           </DialogHeader>
 *           <div className="flex flex-col gap-2">
 *             <Label htmlFor="campaign-name">Name</Label>
 *             <Input id="campaign-name" name="name" defaultValue={name} />
 *           </div>
 *           <DialogFooter>
 *             <DialogClose asChild>
 *               <Button variant="outline">Cancel</Button>
 *             </DialogClose>
 *             <Button type="submit">Save</Button>
 *           </DialogFooter>
 *         </form>
 *       </DialogContent>
 *     </Dialog>
 *   )
 * }
 */
function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const { nesting, handleOpenChange } = useDialogNestingRoot({
    open,
    defaultOpen,
    onOpenChange,
  })

  return (
    <DialogNestingContext.Provider value={nesting}>
      <DialogPrimitive.Root
        data-slot="dialog"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogNestingContext.Provider>
  )
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/25 dark:bg-black/40 backdrop-blur-xs rounded-md ease-out",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
}) {
  const hasOpenChild = useHasOpenChildDialog()

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-child-open={hasOpenChild || undefined}
        // grid-cols-1: an implicit `auto` column grows to the widest no-wrap
        // child (a truncated email, a ledger row) and the dialog clips sideways.
        className={cn(
          "bg-background dark:bg-sidebar border p-5 shadow-2xl radius-full:shadow-soft-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4 fixed top-1/2 left-1/2 z-50 grid grid-cols-1 max-h-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto duration-200 rounded-xl ease-out",
          // Nested dialogs: push this one back + up while a child dialog is open.
          dialogPushBackClasses,
          className
        )}
        {...props}
      >
        <FrameVariantScope variant="flat">{children}</FrameVariantScope>
        {showCloseButton && (
            <DialogPrimitive.Close className="absolute right-3 top-3 p-1.25 rounded-md opacity-70 ring-offset-background transition-all hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:bg-foreground/10 hover:text-foreground cursor-pointer">
              <XIcon className="size-4.75" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
